import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, X, Maximize2, ShieldCheck, RotateCcw, AlertTriangle, Archive, Package, Layers, Activity, TrendingDown, Users } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { track } from "../lib/api";
import { useChat } from "../lib/chatContext";
import { useSuggestedQuestions } from "../lib/queries";
import MD from "./MD";
import VoiceButton from "./VoiceButton";
import PulseWaveform from "./PulseWaveform";
import FeedbackButton from "./FeedbackButton";
import { toolLabel } from "../lib/toolLabels";

// Fallback when the backend's dynamic suggestions are unavailable. Generic
// enough to work for any tenant; the real magic happens when the live
// suggestions arrive with tenant-specific counts and ₪.
const FALLBACK_PROMPTS = [
  { q: "מה הסיכון הגדול ביותר שלי?", icon: "alert", hint: "תלות בלקוחות, פריטים לא פעילים, חוסרי מלאי" },
  { q: "איפה תקוע לי כסף?", icon: "archive", hint: "פריטים שלא נמכרו 60+ ימים" },
  { q: "מה כדאי להזמין השבוע?", icon: "package", hint: "5 פעולות רכש דחופות" },
  { q: "איזו משפחה הכי רווחית?", icon: "layers", hint: "מרג'ין × מחזור" },
  { q: "מה השתנה החודש?", icon: "activity", hint: "חודש מול חודש קודם" },
];

// Context-aware follow-up suggestions. We look at the previous user question
// to pick chips that make sense as a next step. Falls back to generic chips.
const GENERIC_FOLLOWUPS = ["הסבר עוד", "מה הסיכון?", "מה כדאי לעשות?"];
const SKU_FOLLOWUPS = ["מי הספק?", "מתי כדאי להזמין?", "מה ההיסטוריה?"];
const FAMILY_FOLLOWUPS = ["אילו מק״טים מובילים?", "מה ההמלצה לפעולה?", "השווה לחודש קודם"];
const RISK_FOLLOWUPS = ["איך מתקנים את זה?", "מה ההפסד אם לא נטפל?", "מי האחראי?"];

function pickFollowups(prevUserMsg) {
  const text = (prevUserMsg?.content || "").toLowerCase();
  if (prevUserMsg?.context?.label) {
    if (/(מק"ט|מקט|sku)/i.test(prevUserMsg.context.label)) return SKU_FOLLOWUPS;
    return SKU_FOLLOWUPS;
  }
  if (text.includes("משפחת") || text.includes("משפחה")) return FAMILY_FOLLOWUPS;
  if (text.includes("סיכון") || text.includes("חשד") || text.includes("מחיר 0")) return RISK_FOLLOWUPS;
  return GENERIC_FOLLOWUPS;
}

function FollowUpChips({ onPick, previous }) {
  const chips = pickFollowups(previous);
  return (
    <div
      className="mt-2.5 flex flex-wrap gap-1.5"
      data-testid="rail-followups"
    >
      {chips.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          className="text-[10px] px-2 py-1 rounded-full border border-primary/25 bg-primary/5 text-primary/90 hover:border-primary/50 hover:bg-primary/10 transition-colors"
          data-testid={`rail-followup-${c.slice(0, 10)}`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

const ICON_MAP = {
  alert: AlertTriangle,
  archive: Archive,
  package: Package,
  layers: Layers,
  activity: Activity,
  sparkles: Sparkles,
  "trending-down": TrendingDown,
  users: Users,
};

/**
 * Persistent left-edge AI Copilot rail. Mounted by Layout.
 * On mobile collapses to a FAB → bottom-sheet.
 * Shares conversation state with ChatPage via ChatContext.
 */
export default function AICopilotRail({ embedded = false, fab = false }) {
  const [open, setOpen] = useState(false); // mobile bottom-sheet state
  const [input, setInput] = useState("");
  const { messages, loading, phaseLabel, send: ctxSend, reset } = useChat();
  // Live tenant-specific quick prompts. Falls back to FALLBACK_PROMPTS on
  // error or while loading — the rail must never show empty.
  const { data: sqData } = useSuggestedQuestions();
  const prompts =
    Array.isArray(sqData?.questions) && sqData.questions.length > 0
      ? sqData.questions
      : FALLBACK_PROMPTS;
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // External components (e.g. Insight of the Day "ask AI" button) can dispatch this event.
  // Layout mounts both <AICopilotRail embedded /> (lg+ only) and <AICopilotRail fab />
  // (mobile-only). To avoid firing the prompt twice, each instance binds the listener
  // ONLY while its corresponding viewport matches.
  useEffect(() => {
    const matchesViewport = () => {
      // The embedded rail is rendered inside `xl:block` (≥1280px).
      // The FAB instance is visible below xl. Align the listener to the
      // same breakpoint so every viewport has exactly one active handler.
      const isXl = window.matchMedia("(min-width: 1280px)").matches;
      return embedded ? isXl : !isXl;
    };
    const handler = (e) => {
      if (!matchesViewport()) return; // the other instance will handle it
      const prompt = e?.detail?.prompt;
      const context = e?.detail?.context || null;
      if (prompt) {
        setOpen(true);
        send(prompt, context);
      }
    };
    window.addEventListener("ai-copilot-ask", handler);
    return () => window.removeEventListener("ai-copilot-ask", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  const send = async (text, context = null) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    track("chat_send", "/rail", { question: q });
    await ctxSend(q, "rail", context);
  };

  // If user is already on the full chat page, the rail should not duplicate the conversation.
  if (location.pathname.startsWith("/chat") && !fab) return null;

  const RailContent = (
    <>
      {/* Header */}
      <div className="px-5 py-4 border-b border-border/40 flex items-center gap-2 shrink-0" dir="ltr">
        <div className="inline-flex items-center gap-2.5 text-left">
          <div>
            <div className="text-sm font-bold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              <span className="text-foreground">Pulse</span>
              <span className="text-primary ms-1.5">AI</span>
            </div>
            <div className="text-[10px] text-zinc-500 tracking-wider uppercase">
              {loading ? (phaseLabel || "חושב...") : "Smart Insights · מאזין"}
            </div>
          </div>
          <PulseWaveform width={48} className="shrink-0" />
        </div>
        <div className="flex-1" />
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => reset()}
            className="text-zinc-500 hover:text-primary transition-colors p-1.5"
            title="שיחה חדשה"
            data-testid="rail-new-chat"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Messages scroll area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-3 rail-scroll"
      >
        {messages.length === 0 && !loading && (
          <div className="h-full flex flex-col gap-4" data-testid="rail-empty-state">
            <div className="text-center pt-2">
              <div className="text-xs text-zinc-300 font-medium mb-1">
                שאל אותי על העסק שלך
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                כל תשובה מבוססת על נתוני Priority אמיתיים.
              </div>
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div className="text-[10px] text-zinc-600 uppercase tracking-widest mb-0.5 px-1">
                שאלות מומלצות
              </div>
              {prompts.map((p) => {
                const Icon = ICON_MAP[p.icon] || Sparkles;
                return (
                  <button
                    key={p.q}
                    onClick={() => send(p.q)}
                    disabled={loading}
                    className="group text-right px-3 py-2.5 bg-card border border-border rounded-md hover:border-primary/40 hover:bg-primary/5 transition-all disabled:opacity-50 flex items-start gap-2.5"
                    data-testid={`rail-empty-prompt-${p.q.slice(0, 10)}`}
                  >
                    <div className="w-7 h-7 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                    </div>
                    <div className="flex-1 text-right min-w-0">
                      <div className="text-xs text-zinc-200 group-hover:text-primary transition-colors leading-snug truncate">
                        {p.q}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                        {p.hint}
                      </div>
                    </div>
                    <Send className="w-3 h-3 text-zinc-700 group-hover:text-primary -rotate-45 transition-all mt-1 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          const isLastAssistant =
            m.role === "assistant" &&
            !m.isError &&
            i === messages.length - 1 &&
            !loading;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              data-testid={`rail-msg-${m.role}-${i}`}
            >
              {m.role === "user" ? (
                <div>
                  {m.context?.label && (
                    <div
                      className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-md bg-primary/10 border border-primary/25 text-[10px] text-primary font-mono"
                      data-testid={`rail-context-chip-${i}`}
                    >
                      <span className="w-1 h-1 rounded-full bg-primary" />
                      <span className="truncate max-w-[180px]">{m.context.label}</span>
                      {m.context.sublabel && (
                        <span className="text-primary/60 truncate max-w-[120px]">
                          · {m.context.sublabel}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="bg-card border border-border rounded-md p-3 text-xs text-zinc-300 leading-relaxed">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div className="border-r-2 border-primary/60 pr-3 py-1">
                  <MD text={m.content} className="text-xs text-foreground" />
                  {(m.confidence || m.tools_used?.length) && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500" data-testid={`rail-transparency-${i}`}>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 border border-primary/20 text-primary rounded">
                        <ShieldCheck className="w-2.5 h-2.5" />
                        {m.confidence === "high" ? "ביטחון גבוה" : m.confidence === "medium" ? "בינוני" : "נמוך"}
                      </span>
                      {m.tools_used?.slice(0, 2).map((t, ti) => (
                        <span key={ti} className="text-zinc-500">
                          · {toolLabel(t)}
                        </span>
                      ))}
                      {m.rows_analyzed != null && (
                        <span className="font-mono text-zinc-600">· {m.rows_analyzed} שורות</span>
                      )}
                    </div>
                  )}
                  {isLastAssistant && (
                    <FollowUpChips
                      onPick={(q) => send(q)}
                      previous={messages[i - 1]}
                      data-testid={`rail-followups-${i}`}
                    />
                  )}
                </div>
              )}
            </motion.div>
          );
        })}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="rail-loading-phase">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="transition-opacity">{phaseLabel || "קורא נתונים מהמערכת..."}</span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border shrink-0 space-y-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex items-center gap-1.5 bg-[#09090B] border border-border rounded-md px-2 py-1.5 focus-within:border-primary/50 focus-within:shadow-[0_0_15px_rgba(0,229,255,0.1)] transition-all"
        >
          <VoiceButton
            disabled={loading}
            onTranscribed={(text) => send(text)}
            size="sm"
          />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="שאל את Pulse AI..."
            disabled={loading}
            className="flex-1 bg-transparent text-sm text-foreground placeholder-zinc-600 focus:outline-none px-1"
            data-testid="ai-copilot-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="text-primary disabled:text-zinc-700 transition-colors p-1.5"
            data-testid="ai-copilot-send"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            track("rail_open_full_chat", "/rail", {});
            navigate("/chat");
          }}
          className="w-full flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 hover:text-primary py-1.5 rounded-md hover:bg-primary/5 transition-colors"
          data-testid="rail-open-full-chat"
        >
          <Maximize2 className="w-3 h-3" />
          <span>פתח צ׳אט מלא</span>
        </button>

        <div className="flex items-center justify-center pt-1 border-t border-border/40">
          <FeedbackButton className="py-1.5" />
        </div>
      </div>
    </>
  );

  // Embedded mode: rendered inside Layout flex column as a sibling aside (no fixed positioning)
  if (embedded) {
    return (
      <div
        className="h-screen flex flex-col border-r border-border/40"
        data-testid="ai-copilot-rail"
        style={{
          boxShadow: loading
            ? "inset 1px 0 24px rgba(0,229,255,0.06)"
            : undefined,
          transition: "box-shadow 0.3s",
        }}
      >
        {RailContent}
        <style>{`
          .rail-scroll::-webkit-scrollbar { width: 4px; }
          .rail-scroll::-webkit-scrollbar-track { background: transparent; }
          .rail-scroll::-webkit-scrollbar-thumb { background: #27272A; border-radius: 999px; }
          .rail-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,229,255,0.3); }
          .rail-scroll-h::-webkit-scrollbar { height: 0; }
        `}</style>
      </div>
    );
  }

  // FAB mode: only renders FAB + bottom-sheet for screens <xl
  if (!fab) return null;

  // Hide the FAB on the dedicated chat page — the full chat is already open
  if (location.pathname.startsWith("/chat")) return null;

  return (
    <>
      {/* FAB shown on everything below XL (mobile + tablets + medium laptops) */}
      <button
        onClick={() => setOpen(true)}
        className="xl:hidden fixed bottom-16 left-4 z-30 w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-[0_0_20px_rgba(0,229,255,0.4)] flex items-center justify-center"
        data-testid="ai-copilot-fab"
        aria-label="פתח שיחה עם Pulse"
      >
        <Sparkles className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="xl:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="xl:hidden fixed inset-x-0 bottom-0 z-50 h-[85vh] bg-[#0A0A0E] border-t border-border rounded-t-lg flex flex-col"
              data-testid="ai-copilot-sheet"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 left-3 z-10 text-zinc-500 p-1"
                aria-label="סגור"
              >
                <X className="w-5 h-5" />
              </button>
              {RailContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Custom scrollbar style */}
      <style>{`
        .rail-scroll::-webkit-scrollbar { width: 4px; }
        .rail-scroll::-webkit-scrollbar-track { background: transparent; }
        .rail-scroll::-webkit-scrollbar-thumb { background: #27272A; border-radius: 999px; }
        .rail-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,229,255,0.3); }
        .rail-scroll-h::-webkit-scrollbar { height: 0; }
      `}</style>
    </>
  );
}
